<template>
	<div>
		<div class="container-xl py-3">

		<!-- Loading -->
		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border" role="status"></div>
		</div>

		<template v-if="config && !loading">

			<!-- ── QuoteContextBar ──────────────────────────────────────────── -->
			<section style="border:1px solid #e2e8f0;background:#fff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.07);overflow:hidden;margin:20px;">
			<div style="padding:12px 20px;">
				<div class="d-flex align-items-start flex-wrap gap-4">

					<!-- PROJECT TYPE -->
					<div style="min-width:140px;">
						<div class="cwpe-label mb-1">Project type</div>
						<div class="d-inline-flex border rounded overflow-hidden cwpe-btn-group">
							<button type="button" class="cwpe-ctx-btn" :class="mode === 'new' ? 'cwpe-ctx-btn-primary' : 'cwpe-ctx-btn-idle'" @click="setMode('new')">New</button>
							<button type="button" class="cwpe-ctx-btn border-start" :class="mode === 'upgrade' ? 'cwpe-ctx-btn-upgrade' : 'cwpe-ctx-btn-idle'" @click="setMode('upgrade')">Upgrade</button>
						</div>
						<div class="cwpe-sublabel mt-1">
							<span v-if="mode === 'new'">First-time Town Web customer</span>
							<span v-else style="color:#92400e;">Returning / renewal pricing</span>
						</div>
					</div>

					<!-- CONTRACT TERM -->
					<div style="min-width:140px;">
						<div class="cwpe-label mb-1">Contract term</div>
						<div class="d-inline-flex border rounded overflow-hidden cwpe-btn-group">
							<button
								v-for="y in [1,2,3,4,5]"
								:key="y"
								type="button"
								class="cwpe-ctx-btn"
								:class="[y > 1 ? 'border-start' : '', contractYears === y ? 'cwpe-ctx-btn-dark' : 'cwpe-ctx-btn-idle']"
								@click="setContractYears(y)"
							>{{ y }}y</button>
						</div>
						<div class="cwpe-sublabel mt-1">
							{{ contractYears === 1 ? 'No term discount' : 'Longer terms lock in better annual pricing' }}
						</div>
					</div>

					<!-- PRIOR YEAR ANNUAL (upgrade mode) -->
					<template v-if="mode === 'upgrade'">
						<div style="min-width:220px;max-width:340px;flex:1 1 220px;">
							<div class="cwpe-label cwpe-label-amber mb-1">Prior year annual</div>
							<div class="d-flex align-items-center gap-2">
								<div class="position-relative flex-grow-1">
									<span class="position-absolute top-50 translate-middle-y ps-2 text-secondary cwpe-dollar-sign">$</span>
									<input
										v-model.number="priorAnnualDollars"
										type="number"
										min="0"
										step="100"
										@change="onCtxChanged"
										placeholder="e.g. 2500"
										class="form-control form-control-sm cwpe-upgrade-input ps-4 font-monospace"
									/>
								</div>
								<button v-if="priorAnnualDollars != null" type="button" @click="clearPriorAnnual"
									style="background:none;border:none;padding:0;color:#94a3b8;font-size:16px;line-height:1;cursor:pointer;"
									title="Clear">×</button>
							</div>
						</div>

						<!-- TARGET INCREASE -->
						<div v-if="priorAnnualDollars !== null && priorAnnualDollars !== ''">
							<div class="cwpe-label cwpe-label-amber mb-1">Target increase</div>
							<div class="d-flex align-items-center gap-2 mb-1">
								<input
									v-model.number="targetIncreasePct"
									@input="onCtxChanged"
									type="range"
									min="0"
									max="15"
									step="1"
									class="cwpe-range"
									style="width:128px;"
								/>
								<span class="cwpe-pct-badge">+{{ targetIncreasePct }}%</span>
							</div>
							<div class="cwpe-sublabel">
								Target ≈ {{ fmtMoney(Math.round((priorAnnualDollars || 0) * 100 * (1 + targetIncreasePct / 100))) }}/yr
							</div>
						</div>
					</template>

				</div>
			</div>
			</section>

			<!-- ── Quote Simulator ─────────────────────────────────────────── -->
			<section style="border:1px solid #e2e8f0;background:#fff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.07);overflow:hidden;margin:20px;">
				<header style="padding:12px 20px;border-bottom:1px solid #e2e8f0;">
					<div style="font-size:14px;font-weight:600;color:#0f172a;">Quote simulator</div>
					<div style="font-size:12px;color:#64748b;">Plug in sample inputs — prices update live. Use this to validate adjustments before committing.</div>
				</header>

				<!-- How the math works (collapsible) -->
				<details style="border-bottom:1px solid #e2e8f0;background:rgba(239,246,255,0.4);">
					<summary style="cursor:pointer;padding:10px 20px;font-size:12px;font-weight:500;color:#1e40af;user-select:none;">
						How the math works
					</summary>
					<div style="padding:4px 20px 16px;font-size:12px;color:#334155;line-height:1.6;">
						<p style="margin-bottom:8px;">
							<span style="font-weight:600;">Setup fee</span> (one-time, at signup):
							<code style="font-family:ui-monospace,monospace;background:#fff;padding:1px 6px;border-radius:4px;border:1px solid #e2e8f0;">
								({{ config.formula.setup.description }}) × bundle multiplier
							</code>
						</p>
						<p style="margin-bottom:8px;">
							<span style="font-weight:600;">Annual hosting</span> (recurring, every year):
							<code style="font-family:ui-monospace,monospace;background:#fff;padding:1px 6px;border-radius:4px;border:1px solid #e2e8f0;">
								({{ config.formula.annual.description }}) × bundle multiplier
							</code>
						</p>
						<p style="margin-bottom:8px;">
							<span style="font-weight:600;">Support surcharge</span> applies to annual only:
							<span style="font-family:ui-monospace,monospace;">annual × support%</span>
							(Standard = 0%, other tiers add a flat percent).
						</p>
						<p style="margin-bottom:4px;">
							<span style="font-weight:600;">Adjustment</span> sums up regional factors and applies to <span style="font-family:ui-monospace,monospace;">(base + support)</span>:
						</p>
						<ul style="margin:0 0 0 16px;padding:0;color:#475569;">
							<li><span style="font-weight:500;color:#065f46;">+5%</span> if muni trend beats both county and state trend (outgrowing the region)</li>
							<li><span style="font-weight:500;color:#9f1239;">−5%</span> if muni trend lags both (below regional growth)</li>
							<li><span style="font-weight:500;color:#9f1239;">−5%</span> when SNAP rate ≥ 12% · <span style="font-weight:500;color:#065f46;">+3%</span> when SNAP ≤ 8%</li>
							<li><span style="font-weight:500;">State tier</span>: per-state premium/discount, ranging ≈ <span style="font-family:ui-monospace,monospace;">−6% (WV)</span> to <span style="font-family:ui-monospace,monospace;">+8% (CA / NY / DC)</span></li>
						</ul>
						<p style="margin-top:8px;color:#94a3b8;font-style:italic;margin-bottom:0;">
							First-year total = setup + annual + support + adjustment. "Fit" bundles are filtered by population bands shown below.
						</p>
					</div>
				</details>

				<!-- Two-column grid: left panel + right panel -->
				<div style="display:grid;grid-template-columns:320px 1fr;">

					<!-- Left panel: inputs -->
					<div style="padding:20px;border-right:1px solid #e2e8f0;background:rgba(248,250,252,0.5);display:flex;flex-direction:column;gap:12px;font-size:14px;">

						<!-- Load from municipality -->
						<div ref="muniSearchBox" style="position:relative;padding-bottom:12px;border-bottom:1px solid #e2e8f0;">
							<label style="display:block;font-size:12px;font-weight:500;color:#475569;margin-bottom:4px;">Load from municipality</label>
							<div style="position:relative;">
								<input
									v-model="muniQuery"
									type="text"
									autocomplete="off"
									placeholder="e.g. Chester, WV"
									@input="muniDebounceSearch"
									@focus="muniDebounceSearch"
									@keydown="muniKeydown"
									:disabled="muniLoading"
									style="width:100%;border:1px solid #cbd5e1;border-radius:4px;padding:6px 28px 6px 8px;font-size:14px;outline:none;opacity:1;"
									:style="muniLoading ? 'opacity:0.6;' : ''"
								/>
								<button v-if="muniLoaded" type="button" @click="clearMuniLoaded"
									style="position:absolute;right:6px;top:50%;transform:translateY(-50%);background:none;border:none;color:#94a3b8;font-size:14px;cursor:pointer;padding:0;line-height:1;">×</button>
							</div>
							<div v-if="muniShowDropdown && muniQuery.length >= 3"
								style="position:absolute;z-index:20;left:0;right:0;top:100%;margin-top:4px;border:1px solid #e2e8f0;border-radius:4px;background:#fff;box-shadow:0 4px 12px rgba(0,0,0,0.12);max-height:256px;overflow:auto;">
								<div v-if="muniSearching" style="padding:12px;font-size:12px;color:#64748b;">Searching…</div>
								<div v-else-if="muniSuggestions.length === 0" style="padding:12px;font-size:12px;color:#64748b;">No matches.</div>
								<ul v-else style="list-style:none;margin:0;padding:4px 0;">
									<li v-for="(s, i) in muniSuggestions" :key="s.id"
										@mousedown.prevent="loadFromMunicipality(s)"
										@mouseenter="muniActiveIdx = i"
										:style="'padding:6px 12px;font-size:14px;cursor:pointer;display:flex;align-items:baseline;justify-content:space-between;gap:8px;' + (muniActiveIdx === i ? 'background:#eff6ff;' : '')">
										<span style="color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
											{{ s.full_name || s.name }}<span style="color:#94a3b8;">, {{ s.state }}</span>
											<span v-if="s.county" style="color:#94a3b8;font-size:11px;margin-left:4px;">· {{ s.county }}</span>
										</span>
									</li>
								</ul>
							</div>
							<div v-if="muniLoading" style="margin-top:4px;font-size:11px;color:#64748b;">Loading data…</div>
							<div v-else-if="muniError" style="margin-top:4px;font-size:11px;color:#be123c;">{{ muniError }}</div>
							<div v-else-if="muniLoaded"
								style="margin-top:8px;border-radius:4px;background:#eff6ff;box-shadow:inset 0 0 0 1px #bfdbfe;padding:6px 8px;font-size:11px;color:#1e3a8a;line-height:1.4;">
								Loaded <span style="font-weight:600;">{{ muniLoaded.name }}, {{ muniLoaded.state }}</span>
								<span v-if="muniLoaded.county" style="color:#1d4ed8;">· {{ muniLoaded.county }}</span>
								· tweak the values below to explore.
							</div>
							<div v-else style="margin-top:4px;font-size:11px;color:#64748b;line-height:1.4;">
								Pulls real population, trends, and SNAP rate so you can model against actual data.
							</div>
						</div>

						<!-- Population -->
						<div>
							<label style="display:block;font-size:12px;font-weight:500;color:#475569;margin-bottom:4px;">Population</label>
							<input v-model.number="sim.population" type="number" min="0" step="500"
								@change="runSim"
								style="width:100%;border:1px solid #cbd5e1;border-radius:4px;padding:6px 8px;font-size:14px;font-family:ui-monospace,monospace;" />
							<p style="margin-top:4px;font-size:11px;color:#64748b;line-height:1.4;">
								Drives both setup and annual formulas. +1,000 residents adds
								<span style="font-family:ui-monospace,monospace;">${{ (((config.formula.setup.perPopCents + config.formula.annual.perPopCents) * 1000) / 100).toFixed(0) }}</span>
								to Bundle 1's first-year total.
							</p>
						</div>

						<!-- Trend inputs (2-col) -->
						<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
							<div>
								<label style="display:block;font-size:12px;font-weight:500;color:#475569;margin-bottom:4px;">Muni trend %</label>
								<input v-model.number="sim.populationTrendPct" type="number" step="0.005"
									@change="runSim"
									style="width:100%;border:1px solid #cbd5e1;border-radius:4px;padding:6px 8px;font-size:14px;font-family:ui-monospace,monospace;" />
								<p style="margin-top:4px;font-size:11px;color:#64748b;line-height:1.4;">
									5-yr population change (e.g. <span style="font-family:ui-monospace,monospace;">0.02</span> = +2%). Compared to county &amp; state.
								</p>
							</div>
							<div>
								<label style="display:block;font-size:12px;font-weight:500;color:#475569;margin-bottom:4px;">State</label>
								<input v-model="sim.state" type="text" maxlength="2"
									@change="runSim"
									style="width:100%;border:1px solid #cbd5e1;border-radius:4px;padding:6px 8px;font-size:14px;font-family:ui-monospace,monospace;text-transform:uppercase;" />
								<p style="margin-top:4px;font-size:11px;color:#64748b;line-height:1.4;">
									Sets the state-tier adjustment (premium / discount market).
								</p>
							</div>
						</div>
						<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
							<div>
								<label style="display:block;font-size:12px;font-weight:500;color:#475569;margin-bottom:4px;">County trend %</label>
								<input v-model.number="sim.countyTrendPct" type="number" step="0.005"
									@change="runSim"
									style="width:100%;border:1px solid #cbd5e1;border-radius:4px;padding:6px 8px;font-size:14px;font-family:ui-monospace,monospace;" />
								<p style="margin-top:4px;font-size:11px;color:#64748b;line-height:1.4;">County-level growth for context.</p>
							</div>
							<div>
								<label style="display:block;font-size:12px;font-weight:500;color:#475569;margin-bottom:4px;">State trend %</label>
								<input v-model.number="sim.stateTrendPct" type="number" step="0.005"
									@change="runSim"
									style="width:100%;border:1px solid #cbd5e1;border-radius:4px;padding:6px 8px;font-size:14px;font-family:ui-monospace,monospace;" />
								<p style="margin-top:4px;font-size:11px;color:#64748b;line-height:1.4;">State-level growth.</p>
							</div>
						</div>

						<!-- Growth interpretation card -->
						<div :style="'border-radius:6px;padding:12px;' + toneStyle(growthInterpretation.tone)">
							<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;text-align:center;">
								<div>
									<div style="font-size:9px;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;">Muni</div>
									<div :style="'font-size:14px;font-weight:600;font-family:ui-monospace,monospace;' + trendValueColor(sim.populationTrendPct, Math.max(sim.countyTrendPct, sim.stateTrendPct))">
										{{ fmtPct(sim.populationTrendPct) }}
									</div>
								</div>
								<div style="border-left:1px solid #e2e8f0;">
									<div style="font-size:9px;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;">County</div>
									<div style="font-size:14px;font-weight:600;font-family:ui-monospace,monospace;color:#334155;">{{ fmtPct(sim.countyTrendPct) }}</div>
								</div>
								<div style="border-left:1px solid #e2e8f0;">
									<div style="font-size:9px;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;">State</div>
									<div style="font-size:14px;font-weight:600;font-family:ui-monospace,monospace;color:#334155;">{{ fmtPct(sim.stateTrendPct) }}</div>
								</div>
							</div>
							<div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(0,0,0,0.08);">
								<div style="font-size:12px;font-weight:600;">{{ growthInterpretation.headline }}</div>
								<p style="margin:4px 0 0;font-size:11px;line-height:1.4;opacity:0.9;">{{ growthInterpretation.body }}</p>
							</div>
						</div>

						<!-- SNAP rate -->
						<div>
							<label style="display:block;font-size:12px;font-weight:500;color:#475569;margin-bottom:4px;">SNAP rate (decimal)</label>
							<input v-model.number="sim.snapRate" type="number" step="0.01"
								@change="runSim"
								style="width:100%;border:1px solid #cbd5e1;border-radius:4px;padding:6px 8px;font-size:14px;font-family:ui-monospace,monospace;" />
							<p style="margin-top:4px;font-size:11px;color:#64748b;line-height:1.4;">
								Share of households on SNAP (e.g. <span style="font-family:ui-monospace,monospace;">0.10</span> = 10%).
							</p>
						</div>

						<!-- SNAP interpretation card -->
						<div :style="'border-radius:6px;padding:12px;' + toneStyle(snapInterpretation.tone)">
							<div style="display:flex;align-items:baseline;justify-content:space-between;gap:8px;">
								<div style="font-size:12px;font-weight:600;">{{ snapInterpretation.headline }}</div>
								<div style="font-size:14px;font-family:ui-monospace,monospace;font-weight:600;">{{ fmtPct(sim.snapRate) }}</div>
							</div>
							<p v-if="snapInterpretation.body" style="margin:4px 0 0;font-size:11px;line-height:1.4;opacity:0.9;">{{ snapInterpretation.body }}</p>
							<div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(0,0,0,0.08);font-size:10px;font-family:ui-monospace,monospace;color:#475569;display:flex;justify-content:space-between;">
								<span>low ≤ 8%</span>
								<span>neutral</span>
								<span>high ≥ 12%</span>
							</div>
						</div>

					</div><!-- /left panel -->

					<!-- Right panel: results -->
					<div style="padding:20px;display:flex;flex-direction:column;gap:16px;">
						<div v-if="!simResult" style="font-size:14px;color:#94a3b8;">Calculating…</div>
						<div v-else>

							<!-- Adjustment factor pills -->
							<div v-if="adjustmentFactors.length" style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:16px;">
								<span v-for="f in adjustmentFactors" :key="f.key"
									:style="'border-radius:4px;padding:2px 8px;font-size:12px;' + (
										f.deltaPct > 0 ? 'background:#ecfdf5;color:#065f46;box-shadow:inset 0 0 0 1px #a7f3d0;' :
										f.deltaPct < 0 ? 'background:#fff1f2;color:#9f1239;box-shadow:inset 0 0 0 1px #fecdd3;' :
										'background:#f1f5f9;color:#334155;box-shadow:inset 0 0 0 1px #e2e8f0;')">
									{{ f.label }} {{ f.deltaPct >= 0 ? '+' : '' }}{{ (f.deltaPct * 100).toFixed(0) }}%
								</span>
							</div>

							<!-- Bundle results table -->
							<table style="width:100%;font-size:12px;border-collapse:collapse;">
								<thead>
									<tr style="color:#64748b;">
										<th style="text-align:left;font-weight:500;padding:6px 0 6px 0;">Bundle</th>
										<th style="text-align:right;font-weight:500;padding:6px 8px;">
											Setup
											<div style="font-size:9px;font-weight:400;color:#94a3b8;">base → adjusted</div>
										</th>
										<th style="text-align:right;font-weight:500;padding:6px 8px;background:rgba(209,250,229,0.3);">
											Annual
											<div style="font-size:9px;font-weight:400;color:rgba(4,120,87,0.7);">base → adjusted · /yr</div>
										</th>
										<th style="text-align:right;font-weight:500;padding:6px 8px;">First-year</th>
										<th style="padding:6px 0;width:36px;"></th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="b in simResult.bundles" :key="b.bundleId"
										@click="toggleBundle(b.bundleId)"
										:style="'border-top:1px solid #e2e8f0;cursor:pointer;' + (
											selectedBundleId === b.bundleId
												? 'outline:2px solid #2563eb;outline-offset:-2px;box-shadow:0 1px 3px rgba(0,0,0,0.08);'
												: isBundleRecommended(b.bundleId)
													? 'background:rgba(239,246,255,0.4);'
													: 'opacity:0.7;'
										)">
										<td style="padding:6px 8px 6px 0;">
											<div style="font-weight:500;color:#0f172a;">{{ b.bundleName }}</div>
											<div style="font-size:10px;color:#94a3b8;">×{{ b.multiplier ? b.multiplier.toFixed(3) : '' }}</div>
										</td>
										<td style="padding:6px 8px;text-align:right;font-family:ui-monospace,monospace;">
											<div v-if="b.adjustmentPct !== 0" style="font-size:10px;color:#94a3b8;">base {{ fmtMoney(b.baseSetupCents) }}</div>
											<div :style="'font-weight:600;' + (b.adjustmentPct > 0 ? 'color:#065f46;' : b.adjustmentPct < 0 ? 'color:#be123c;' : 'color:#334155;')">
												{{ fmtMoney(b.setupCents) }}
												<span v-if="b.adjustmentPct !== 0" style="font-size:9px;font-weight:400;">
													({{ b.adjustmentPct > 0 ? '+' : '' }}{{ (b.adjustmentPct * 100).toFixed(0) }}%)
												</span>
											</div>
										</td>
										<td style="padding:6px 8px;text-align:right;font-family:ui-monospace,monospace;background:rgba(209,250,229,0.3);">
											<div v-if="b.adjustmentPct !== 0" style="font-size:10px;color:#475569;">base {{ fmtMoney(b.baseAnnualCents) }}</div>
											<div :style="'font-weight:600;' + (b.adjustmentPct > 0 ? 'color:#065f46;' : b.adjustmentPct < 0 ? 'color:#be123c;' : 'color:#1e293b;')">
												{{ fmtMoney(b.annualCents) }}
												<span v-if="b.adjustmentPct !== 0" style="font-size:9px;font-weight:400;">
													({{ b.adjustmentPct > 0 ? '+' : '' }}{{ (b.adjustmentPct * 100).toFixed(0) }}%)
												</span>
											</div>
										</td>
										<td style="padding:6px 8px;text-align:right;font-family:ui-monospace,monospace;">
											<div v-if="b.adjustmentPct !== 0" style="font-size:10px;color:#94a3b8;">base {{ fmtMoney(b.baseSetupCents + b.baseAnnualCents) }}</div>
											<div :style="'font-weight:600;' + (b.adjustmentPct > 0 ? 'color:#065f46;' : b.adjustmentPct < 0 ? 'color:#be123c;' : 'color:#0f172a;')">
												{{ fmtMoney(b.firstYearCents) }}
												<span v-if="b.adjustmentPct !== 0" style="font-size:9px;font-weight:400;">
													({{ b.adjustmentPct > 0 ? '+' : '' }}{{ (b.adjustmentPct * 100).toFixed(0) }}%)
												</span>
											</div>
										</td>
										<td style="padding:6px 0 6px 8px;">
											<span v-if="isBundleRecommended(b.bundleId)"
												style="border-radius:999px;background:#2563eb;color:#fff;padding:2px 6px;font-size:9px;text-transform:uppercase;font-weight:600;">fit</span>
										</td>
									</tr>
								</tbody>
							</table>

						</div>

						<!-- Formula note -->
						<div style="border:1px dashed #cbd5e1;border-radius:6px;background:#f8fafc;padding:10px 12px;font-size:12px;color:#475569;">
							Pricing-formula knobs (base rates, state tiers, SNAP thresholds, line-item overrides) live on the
							<router-link to="/cwpe/settings" style="color:#1d4ed8;font-weight:500;">Pricing formula →</router-link>
						</div>
					</div><!-- /right panel -->
				</div>
			</section>

			<!-- ── Competitor comparison ────────────────────────────────────── -->
			<section style="border:1px solid #e2e8f0;background:#fff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.07);overflow:hidden;margin:0 20px 20px;">

				<!-- Header -->
				<header style="padding:12px 20px;border-bottom:1px solid #e2e8f0;display:flex;align-items:baseline;justify-content:space-between;gap:12px;flex-wrap:wrap;">
					<div>
						<div style="font-size:14px;font-weight:600;color:#0f172a;">Competitor comparison</div>
						<p style="font-size:12px;color:#64748b;margin:0;">
							<span v-if="selectedBundle" style="font-weight:500;color:#92400e;">{{ selectedBundle.name }}</span>
							priced with the same regional context as the simulator.
							<span style="font-weight:500;color:#047857;">Win</span> = we undercut every visible competitor;
							<span style="font-weight:500;color:#be123c;">Over</span> = we're more expensive than all of them;
							<span style="font-weight:500;color:#b45309;">Split</span> = mixed.
						</p>
					</div>
					<div style="display:flex;align-items:center;gap:6px;font-size:11px;">
						<span :style="'border-radius:4px;padding:2px 6px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;' + (mode === 'upgrade' ? 'background:#fef3c7;color:#92400e;box-shadow:inset 0 0 0 1px #fcd34d;' : 'background:#f1f5f9;color:#334155;box-shadow:inset 0 0 0 1px #cbd5e1;')">
							{{ mode === 'upgrade' ? 'Upgrade' : 'New' }}
						</span>
						<span style="border-radius:4px;padding:2px 6px;background:#f1f5f9;color:#334155;box-shadow:inset 0 0 0 1px #cbd5e1;font-family:ui-monospace,monospace;">
							{{ contractYears }}y term
						</span>
					</div>
				</header>

				<!-- Segment strip -->
				<div v-if="competitors.length" style="padding:8px 20px;background:#f8fafc;border-bottom:1px solid #e2e8f0;font-size:12px;display:flex;align-items:center;gap:16px;flex-wrap:wrap;">
					<div v-for="seg in visibleBySegment" :key="seg.segment" style="display:flex;align-items:baseline;gap:8px;">
						<span style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;font-weight:600;">{{ seg.label }}</span>
						<span style="color:#334155;">{{ seg.names.join(' · ') }}</span>
					</div>
					<span v-if="!visibleCompetitors.length" style="color:#64748b;font-style:italic;">
						No competitors in segment at pop {{ sim.population ? sim.population.toLocaleString() : '—' }}.
					</span>
					<label style="margin-left:auto;display:flex;align-items:center;gap:6px;cursor:pointer;font-size:12px;color:#475569;">
						<input type="checkbox" v-model="showOutOfSegment" style="width:14px;height:14px;" />
						Show out-of-segment ({{ hiddenCompetitors.length }})
					</label>
				</div>

				<!-- Hidden competitors note -->
				<div v-if="hiddenCompetitors.length && !showOutOfSegment"
					style="padding:6px 20px;font-size:11px;color:#64748b;border-bottom:1px solid #e2e8f0;background:rgba(248,250,252,0.6);">
					Hidden at pop {{ sim.population ? sim.population.toLocaleString() : '—' }}:
					<span v-for="(c, i) in hiddenCompetitors" :key="c.id">
						<span style="font-weight:500;color:#475569;">{{ c.name }}</span>
						<span style="color:#94a3b8;">
							({{ c.segment }}, range {{ c.minPopulation.toLocaleString() }}–{{ c.maxPopulation ? c.maxPopulation.toLocaleString() : '∞' }})
						</span>
						<span v-if="i < hiddenCompetitors.length - 1"> · </span>
					</span>
				</div>

				<!-- Tier + horizon controls -->
				<div style="padding:8px 20px;border-bottom:1px solid #e2e8f0;background:#f8fafc;display:flex;align-items:center;gap:16px;flex-wrap:wrap;font-size:12px;">
					<div style="display:flex;align-items:center;gap:8px;">
						<span style="color:#475569;font-weight:500;">Town Web tier:</span>
						<select v-model="selectedBundleId"
							style="border:1px solid #cbd5e1;border-radius:4px;background:#fff;padding:4px 8px;font-size:12px;font-weight:500;">
							<option v-for="b in config.bundles" :key="b.id" :value="b.id">{{ b.name }}</option>
						</select>
					</div>
					<div style="display:flex;align-items:center;gap:8px;">
						<span style="color:#475569;font-weight:500;">Contract horizon:</span>
						<div style="display:inline-flex;border:1px solid #cbd5e1;border-radius:4px;overflow:hidden;">
							<button v-for="(yr, i) in [1, 3, 4, 5]" :key="yr" type="button" @click="horizon = yr"
								:style="'padding:4px 12px;font-weight:500;border:none;cursor:pointer;font-size:12px;' + (i > 0 ? 'border-left:1px solid #cbd5e1;' : '') + (horizon === yr ? 'background:#d97706;color:#fff;' : 'background:#fff;color:#334155;')">
								{{ yr }}-year
							</button>
						</div>
					</div>
				</div>

				<!-- Town Web bundle-annualized strip -->
				<div v-if="tierAnnualized.length" style="padding:12px 20px;border-bottom:1px solid #e2e8f0;background:rgba(255,251,235,0.4);">
					<div style="display:flex;align-items:baseline;justify-content:space-between;margin-bottom:8px;flex-wrap:wrap;gap:8px;">
						<div style="font-size:11px;color:#334155;font-weight:500;">
							Town Web at pop {{ sim.population ? sim.population.toLocaleString() : '—' }} — {{ horizon > 1 ? `effective $/yr over ${horizon} yrs` : 'first-year $/yr' }}
						</div>
						<div style="font-size:10px;color:#64748b;">Click a tier to pick it · apples-to-apples with the per-competitor $/yr below</div>
					</div>
					<div style="display:grid;grid-template-columns:repeat(5,1fr);gap:8px;">
						<button v-for="t in tierAnnualized" :key="t.bundleId" type="button"
							@click="selectedBundleId = t.bundleId"
							:style="'text-align:left;border-radius:6px;padding:8px 10px;font-size:12px;cursor:pointer;' + (
								selectedBundleId === t.bundleId
									? 'border:2px solid #2563eb;background:#eff6ff;box-shadow:0 1px 2px rgba(0,0,0,0.05),0 0 0 2px #93c5fd;'
									: t.onTarget
										? 'border:2px solid #10b981;background:#ecfdf5;'
										: 'border:2px solid #fde68a;background:#fff;'
							)">
							<div style="display:flex;align-items:baseline;justify-content:space-between;">
								<span style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;font-weight:600;">{{ t.bundleId }}</span>
								<span v-if="selectedBundleId === t.bundleId"
									style="font-size:9px;text-transform:uppercase;letter-spacing:0.05em;background:#2563eb;color:#fff;border-radius:999px;padding:1px 6px;">Selected</span>
								<span v-else-if="t.onTarget"
									style="font-size:9px;text-transform:uppercase;letter-spacing:0.05em;background:#059669;color:#fff;border-radius:999px;padding:1px 6px;">On target</span>
							</div>
							<div style="font-size:11px;font-weight:500;color:#334155;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:1.2;margin-top:2px;">{{ t.bundleName }}</div>
							<div style="margin-top:4px;font-family:ui-monospace,monospace;font-size:14px;color:#065f46;font-weight:600;">
								{{ fmtMoney(horizon > 1 ? t.effectiveAnnualCents : t.firstYearCents) }}/yr
							</div>
							<div v-if="horizon > 1" style="font-size:9px;color:#94a3b8;line-height:1.2;">
								setup {{ fmtMoney(t.setupCents) }} + {{ fmtMoney(t.annualCents) }}/yr × {{ horizon }}
							</div>
							<div v-if="mode === 'upgrade' && t.deltaVsPriorYearPct != null"
								:style="'font-size:9px;font-family:ui-monospace,monospace;font-weight:600;margin-top:2px;' + (t.deltaVsPriorYearPct >= 0 ? 'color:#047857;' : 'color:#be123c;')">
								Δ {{ t.deltaVsPriorYearPct >= 0 ? '+' : '' }}{{ (t.deltaVsPriorYearPct * 100).toFixed(1) }}% vs prior
							</div>
						</button>
					</div>
				</div>

				<!-- Target-range bar (upgrade mode) -->
				<div v-if="targetRange" style="padding:16px 20px;border-bottom:1px solid #e2e8f0;background:rgba(236,253,245,0.4);">
					<div style="display:flex;align-items:baseline;justify-content:space-between;margin-bottom:8px;flex-wrap:wrap;gap:8px;">
						<div style="font-size:11px;font-weight:600;color:#065f46;">
							Target range — prior {{ fmtMoney(targetRange.priorCents) }}/yr · target {{ fmtMoney(targetRange.targetCents) }}/yr
							({{ targetIncreasePct }}% uplift, ±3%)
						</div>
						<div style="font-size:10px;color:#64748b;">Bundle dots inside the shaded band are on-target.</div>
					</div>
					<div style="position:relative;height:64px;margin:0 4px;">
						<!-- Track -->
						<div style="position:absolute;left:0;right:0;top:32px;height:4px;border-radius:2px;background:#e2e8f0;"></div>
						<!-- Tolerance band -->
						<div style="position:absolute;top:28px;height:12px;border-radius:2px;background:rgba(52,211,153,0.6);box-shadow:inset 0 0 0 1px #34d399;"
							:style="{ left: targetRange.toleranceLeftPct + '%', width: (targetRange.toleranceRightPct - targetRange.toleranceLeftPct) + '%' }"></div>
						<!-- Prior anchor -->
						<div style="position:absolute;top:16px;bottom:8px;width:2px;background:#64748b;left:0%;"></div>
						<div style="position:absolute;top:0;left:0%;font-size:9px;font-family:ui-monospace,monospace;color:#475569;">
							{{ targetRange.targetLeftPct >= 5 ? 'prior' : 'prior / target' }}
						</div>
						<!-- Target marker -->
						<div style="position:absolute;top:16px;bottom:8px;width:2px;background:#047857;"
							:style="{ left: targetRange.targetLeftPct + '%' }"></div>
						<div v-if="targetRange.targetLeftPct >= 5"
							style="position:absolute;top:0;font-size:9px;font-family:ui-monospace,monospace;color:#047857;font-weight:600;transform:translateX(-50%);"
							:style="{ left: targetRange.targetLeftPct + '%' }">target</div>
						<!-- Competitor ticks -->
						<div v-for="tick in targetRange.competitorTicks" :key="tick.name"
							style="position:absolute;top:28px;width:2px;height:12px;background:rgba(100,116,139,0.6);"
							:style="{ left: tick.leftPct + '%' }"
							:title="`${tick.name}: ${fmtMoney(tick.cents)}/yr`"></div>
						<!-- Bundle dots -->
						<div v-for="d in targetRange.dots" :key="d.bundleId"
							style="position:absolute;"
							:style="{ left: d.leftPct + '%', top: d.onTarget ? '22px' : '32px' }">
							<button type="button" @click="selectedBundleId = d.bundleId"
								:style="'transform:translateX(-50%);border-radius:999px;border:none;padding:0;min-width:0;line-height:1;outline:none;display:flex;align-items:center;justify-content:center;cursor:pointer;' + (
									selectedBundleId === d.bundleId
										? 'width:20px;height:20px;background:#2563eb;box-shadow:0 0 0 2px #fff, 0 4px 6px rgba(0,0,0,0.15);'
										: d.onTarget
											? 'width:16px;height:16px;background:#059669;box-shadow:0 0 0 2px #fff;'
											: 'width:14px;height:14px;background:#d97706;box-shadow:0 0 0 2px #fff;'
								)"
								:title="`${d.bundleName}: ${fmtMoney(d.firstYearCents)}${d.onTarget ? ' · on target' : ''}`">
								<span style="font-size:8px;font-weight:700;color:#fff;text-transform:uppercase;line-height:1;">{{ d.bundleId }}</span>
							</button>
						</div>
					</div>
					<div style="display:flex;justify-content:space-between;margin-top:4px;font-size:9px;font-family:ui-monospace,monospace;color:#64748b;">
						<span>{{ fmtMoney(targetRange.priorCents) }}</span>
						<span>{{ fmtMoney(targetRange.maxCents) }}</span>
					</div>
				</div>

				<!-- Comparison table -->
				<div style="overflow-x:auto;">
					<table v-if="visibleCompetitors.length" style="width:100%;font-size:12px;border-collapse:collapse;">
						<thead style="background:#f8fafc;color:#475569;">
							<tr>
								<th style="text-align:left;padding:8px 16px;font-weight:500;" rowspan="2">Population</th>
								<th style="text-align:center;padding:8px 12px;font-weight:500;" rowspan="2">Verdict</th>
								<th style="text-align:right;padding:8px 16px;font-weight:500;border-left:1px solid #e2e8f0;background:rgba(255,251,235,0.6);" colspan="3">Town Web</th>
								<th v-for="c in visibleCompetitors" :key="c.id"
									style="text-align:right;padding:8px 16px;font-weight:500;border-left:1px solid #e2e8f0;" colspan="3">
									<div style="display:flex;align-items:baseline;justify-content:flex-end;gap:6px;">
										<a :href="c.url" target="_blank" rel="noreferrer" style="text-decoration:underline;color:#334155;">{{ c.name }}</a>
										<span v-if="c.pricingStatus === 'estimated'"
											style="display:inline-block;border-radius:4px;padding:1px 6px;font-size:9px;font-weight:700;text-transform:uppercase;background:#fef3c7;color:#92400e;box-shadow:inset 0 0 0 1px #fcd34d;"
											title="Industry-estimate pricing — not a verified quote">Est.</span>
										<span v-else-if="c.pricingStatus === 'placeholder'"
											style="display:inline-block;border-radius:4px;padding:1px 6px;font-size:9px;font-weight:700;text-transform:uppercase;background:#e2e8f0;color:#334155;box-shadow:inset 0 0 0 1px #cbd5e1;"
											title="No pricing data yet">TBD</span>
									</div>
									<div style="font-size:9px;color:#64748b;font-weight:400;font-style:italic;">{{ c.segment === 'small-muni' ? 'Small-muni' : 'Mid-tier' }}</div>
								</th>
							</tr>
							<tr style="font-size:10px;color:#64748b;">
								<th style="text-align:right;padding:4px 8px;font-weight:400;border-left:1px solid #e2e8f0;background:rgba(255,251,235,0.6);">Setup</th>
								<th style="text-align:right;padding:4px 8px;font-weight:400;background:rgba(255,251,235,0.6);">Annual</th>
								<th style="text-align:right;padding:4px 8px;font-weight:600;background:rgba(255,251,235,0.6);"
									:title="`Setup + annual × ${horizon} | annualized = ÷ ${horizon}`">
									{{ horizon > 1 ? `${horizon}-yr total` : 'First-year' }}
									<span style="display:block;font-size:9px;font-weight:400;color:#94a3b8;font-style:italic;">+ annualized $/yr</span>
								</th>
								<template v-for="c in visibleCompetitors">
									<th :key="c.id + '-s'" style="text-align:right;padding:4px 8px;font-weight:400;border-left:1px solid #e2e8f0;">Setup</th>
									<th :key="c.id + '-a'" style="text-align:right;padding:4px 8px;font-weight:400;">Annual</th>
									<th :key="c.id + '-t'" style="text-align:right;padding:4px 8px;font-weight:600;"
										:title="`Setup + annual × ${horizon} | annualized = ÷ ${horizon}`">
										{{ horizon > 1 ? `${horizon}-yr` : 'First-yr' }}
										<span style="display:block;font-size:9px;font-weight:400;color:#94a3b8;font-style:italic;">+ annualized $/yr</span>
									</th>
								</template>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(row, rowIdx) in compRows" :key="rowIdx"
								:style="row.isActual
									? 'border-top:2px solid #3b82f6;border-bottom:2px solid #3b82f6;background:rgba(239,246,255,0.6);font-weight:500;'
									: 'border-top:1px solid #e2e8f0;' + (
										verdictForPop(row.pop) === 'beaten' ? 'background:#fff1f2;' :
										verdictForPop(row.pop) === 'undercut' ? 'background:#ecfdf5;' : ''
									)">
								<td :style="'padding:8px 16px;font-family:ui-monospace,monospace;' + (row.isActual ? 'color:#1e3a8a;font-weight:600;border-left:2px solid #3b82f6;' : 'color:#334155;')">
									<span v-if="row.isActual"
										style="display:inline-block;border-radius:4px;background:#2563eb;color:#fff;padding:1px 6px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;margin-right:4px;vertical-align:middle;">Actual</span>
									{{ row.label }}
								</td>
								<td style="padding:8px 12px;text-align:center;">
									<span v-if="verdictForPop(row.pop) === 'undercut'"
										style="display:inline-block;border-radius:4px;padding:2px 6px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;background:#059669;color:#fff;">Win</span>
									<span v-else-if="verdictForPop(row.pop) === 'beaten'"
										style="display:inline-block;border-radius:4px;padding:2px 6px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;background:#e11d48;color:#fff;"
										title="More expensive than every competitor shown">Over</span>
									<span v-else-if="verdictForPop(row.pop) === 'split'"
										style="display:inline-block;border-radius:4px;padding:2px 6px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;background:#fef3c7;color:#92400e;box-shadow:inset 0 0 0 1px #fcd34d;">Split</span>
								</td>
								<!-- Town Web block -->
								<td style="padding:8px 8px;text-align:right;font-family:ui-monospace,monospace;color:#475569;border-left:1px solid #e2e8f0;background:rgba(255,251,235,0.3);vertical-align:top;">
									{{ fmtMoney(compOurPrice(row.pop) ? compOurPrice(row.pop).setupCents : null) }}
								</td>
								<td style="padding:8px 8px;text-align:right;font-family:ui-monospace,monospace;color:#1e293b;background:rgba(255,251,235,0.3);vertical-align:top;">
									{{ fmtMoney(compOurPrice(row.pop) ? compOurPrice(row.pop).annualCents : null) }}
								</td>
								<td style="padding:8px 8px;text-align:right;font-family:ui-monospace,monospace;font-weight:600;color:#0f172a;background:rgba(255,251,235,0.3);vertical-align:top;">
									<div>{{ fmtMoney(ourHorizonTotal(row.pop)) }}</div>
									<div style="font-size:9px;color:#065f46;font-weight:400;font-style:italic;line-height:1.2;">
										≈ {{ fmtMoney(ourEffectiveAnnual(row.pop)) }}/yr
									</div>
								</td>
								<!-- Per-competitor blocks -->
								<template v-for="c in visibleCompetitors">
									<td :key="c.id + '-s-' + rowIdx" style="position:relative;padding:8px 8px;text-align:right;font-family:ui-monospace,monospace;color:#334155;border-left:1px solid #e2e8f0;">
										<template v-if="competitorBand(c, row.pop) && competitorBand(c, row.pop).setupCents">
											{{ fmtMoney(competitorBand(c, row.pop).setupCents) }}<sup v-if="c.pricingStatus !== 'verified'">*</sup>
										</template>
										<span v-else style="color:#94a3b8;">—</span>
									</td>
									<td :key="c.id + '-a-' + rowIdx" :style="'padding:8px 8px;text-align:right;font-family:ui-monospace,monospace;' + stanceStyle(computeDelta(compOurPrice(row.pop) ? compOurPrice(row.pop).annualCents : null, competitorBand(c, row.pop) ? competitorBand(c, row.pop).annualCents : null))">
										<template v-if="competitorBand(c, row.pop)">
											{{ fmtMoney(competitorBand(c, row.pop).annualCents) }}<sup v-if="c.pricingStatus !== 'verified'">*</sup>
										</template>
										<span v-else style="color:#94a3b8;">—</span>
									</td>
									<td :key="c.id + '-t-' + rowIdx" :style="'padding:8px 8px;text-align:right;font-family:ui-monospace,monospace;font-weight:600;vertical-align:top;' + stanceStyle(computeDelta(ourHorizonTotal(row.pop), competitorHorizonTotal(c, row.pop)))">
										<template v-if="competitorBand(c, row.pop)">
											<div>{{ fmtMoney(competitorHorizonTotal(c, row.pop)) }}<sup v-if="c.pricingStatus !== 'verified'">*</sup></div>
											<div style="font-size:9px;color:#475569;font-weight:400;font-style:italic;line-height:1.2;">
												≈ {{ fmtMoney(competitorEffectiveAnnual(c, row.pop)) }}/yr
											</div>
										</template>
										<span v-else style="color:#94a3b8;">—</span>
									</td>
								</template>
							</tr>
						</tbody>
					</table>
					<div v-else style="padding:32px 20px;text-align:center;font-size:14px;color:#64748b;">
						No competitors configured for this population tier. Toggle "Show out-of-segment" to browse all vendors.
					</div>
				</div>

				<!-- Insights -->
				<div v-if="competitorInsights.length" style="padding:12px 20px;border-top:1px solid #e2e8f0;background:#f8fafc;font-size:12px;color:#334155;">
					<div v-for="(msg, i) in competitorInsights" :key="i" style="line-height:1.4;margin-bottom:2px;">{{ msg }}</div>
				</div>

				<!-- Estimated footnote -->
				<div v-if="anyEstimated" style="padding:8px 20px;border-top:1px solid #e2e8f0;background:rgba(255,251,235,0.5);font-size:11px;color:#78350f;">
					<span style="font-weight:600;">* Industry estimate — verify before quoting.</span>
					Mid-tier vendors ({{ visibleCompetitors.filter(c => c.pricingStatus !== 'verified').map(c => c.name).join(', ') }})
					don't yet have firm quote data in our database. Cells with a <code>*</code> carry directional numbers from public rate cards / RFP debriefs;
					treat them as ballpark figures until a real RFP response confirms.
				</div>

				<!-- Per-competitor verification stamps -->
				<div style="padding:8px 20px;border-top:1px solid #e2e8f0;font-size:10px;color:#64748b;display:flex;flex-wrap:wrap;gap:12px;">
					<span v-for="c in visibleCompetitors" :key="c.id">
						<a :href="c.url" target="_blank" style="text-decoration:underline;color:#64748b;">{{ c.name }}</a>
						<span style="color:#94a3b8;"> ({{ c.pricingStatus === 'verified' ? 'verified' : c.pricingStatus }} {{ c.lastVerified }})</span>
					</span>
				</div>
			</section>

			<!-- ── Bundle 1 price grid ──────────────────────────────────────── -->
			<section style="border:1px solid #e2e8f0;background:#fff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.07);overflow:hidden;margin:0 20px 20px;">
				<header style="padding:12px 20px;border-bottom:1px solid #e2e8f0;">
					<div style="font-size:14px;font-weight:600;color:#0f172a;">Bundle 1 prices by population</div>
					<p style="font-size:12px;color:#64748b;font-family:ui-monospace,monospace;margin:0;">
						Setup = {{ config.formula.setup.description }} · Annual = {{ config.formula.annual.description }}. Higher bundles multiply.
					</p>
				</header>
				<table style="width:100%;font-size:12px;border-collapse:collapse;">
					<thead style="background:#f8fafc;color:#475569;">
						<tr>
							<th style="text-align:left;padding:8px 16px;font-weight:500;">Population</th>
							<th style="text-align:right;padding:8px 16px;font-weight:500;">Setup <span style="font-size:9px;font-weight:400;color:#94a3b8;">one-time</span></th>
							<th style="text-align:right;padding:8px 16px;font-weight:500;background:rgba(209,250,229,0.3);">Annual <span style="font-size:9px;font-weight:400;color:rgba(4,120,87,0.7);">/yr</span></th>
							<th style="text-align:right;padding:8px 16px;font-weight:500;">First year</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in config.priceGrid" :key="row.population" style="border-top:1px solid #e2e8f0;">
							<td style="padding:6px 16px;font-family:ui-monospace,monospace;color:#334155;">{{ row.population.toLocaleString('en-US') }}</td>
							<td style="padding:6px 16px;text-align:right;font-family:ui-monospace,monospace;color:#334155;">{{ fmtMoney(row.prices[0].setupCents) }}</td>
							<td style="padding:6px 16px;text-align:right;font-family:ui-monospace,monospace;font-weight:600;color:#065f46;background:rgba(209,250,229,0.2);">{{ fmtMoney(row.prices[0].annualCents) }}</td>
							<td style="padding:6px 16px;text-align:right;font-family:ui-monospace,monospace;font-weight:600;color:#0f172a;">{{ fmtMoney(row.prices[0].firstYearCents) }}</td>
						</tr>
					</tbody>
				</table>
			</section>

			<!-- ── Three-column grid: Relevance bands + Support tiers + À la carte ── -->
			<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;margin:0 20px 20px;">

				<section style="border:1px solid #e2e8f0;background:#fff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.07);padding:20px;">
					<div style="font-size:14px;font-weight:600;color:#0f172a;margin-bottom:8px;">Relevance bands</div>
					<p style="font-size:12px;color:#64748b;margin-bottom:12px;">Bundles highlighted as "fit" on the lookup screen, by population range.</p>
					<ul style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:8px;">
						<li v-for="band in config.relevanceBands" :key="band.minPop" style="display:flex;justify-content:space-between;gap:8px;font-size:12px;">
							<span style="font-family:ui-monospace,monospace;color:#334155;">≥ {{ band.minPop.toLocaleString('en-US') }}</span>
							<span style="display:flex;gap:4px;flex-wrap:wrap;justify-content:flex-end;">
								<span v-for="id in band.recommendedBundleIds" :key="id"
									style="border-radius:4px;padding:2px 6px;background:#eff6ff;color:#1e40af;box-shadow:inset 0 0 0 1px #bfdbfe;text-transform:uppercase;font-size:10px;font-weight:500;">
									{{ id }}
								</span>
							</span>
						</li>
					</ul>
				</section>

				<section style="border:1px solid #e2e8f0;background:#fff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.07);padding:20px;">
					<div style="font-size:14px;font-weight:600;color:#0f172a;margin-bottom:8px;">Support tiers</div>
					<ul style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:6px;">
						<li v-for="t in config.supportTiers" :key="t.id" style="display:flex;justify-content:space-between;font-size:12px;">
							<span>{{ t.name }}</span>
							<span style="font-family:ui-monospace,monospace;color:#334155;">
								{{ t.surcharge === 0 ? 'included' : `+${(t.surcharge * 100).toFixed(0)}%` }}
							</span>
						</li>
					</ul>
				</section>

				<section style="border:1px solid #e2e8f0;background:#fff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.07);padding:20px;">
					<div style="font-size:14px;font-weight:600;color:#0f172a;margin-bottom:8px;">À la carte</div>
					<ul style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:6px;">
						<li v-for="it in config.alaCarte" :key="it.id" style="display:flex;justify-content:space-between;gap:12px;font-size:12px;">
							<span>
								{{ it.name }}
								<span style="color:#94a3b8;font-size:10px;">({{ it.unit }})</span>
							</span>
							<span style="font-family:ui-monospace,monospace;color:#334155;white-space:nowrap;">{{ fmtMoney(it.priceCents) }}</span>
						</li>
					</ul>
				</section>
			</div>

			<!-- ── Bundle contents ──────────────────────────────────────────── -->
			<section style="margin:0 20px 20px;">
				<div style="font-size:14px;font-weight:600;color:#0f172a;margin-bottom:12px;">Bundle contents</div>
				<div style="display:grid;grid-template-columns:repeat(5,1fr);gap:12px;">
					<div v-for="b in config.bundles" :key="b.id"
						style="border:1px solid #e2e8f0;background:#fff;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,0.07);padding:16px;">
						<div style="font-size:10px;text-transform:uppercase;letter-spacing:0.1em;color:#64748b;">{{ b.id.toUpperCase() }} · ×{{ b.multiplier.toFixed(2) }}</div>
						<div style="font-size:14px;font-weight:600;color:#0f172a;margin-top:2px;line-height:1.2;">{{ b.name }}</div>
						<p style="font-size:12px;color:#64748b;margin:8px 0 12px;line-height:1.4;">{{ b.description }}</p>
						<ul style="margin:0;padding:0 0 0 16px;font-size:11px;color:#334155;display:flex;flex-direction:column;gap:2px;">
							<li v-for="it in b.includedItems" :key="it" style="list-style-type:disc;color:#64748b;">
								<span style="color:#334155;">{{ it }}</span>
							</li>
						</ul>
					</div>
				</div>
			</section>

		</template><!-- /config -->

		</div><!-- /container-xl -->
	</div>
</template>

<script>
import restApi from '../../api/index.js'

const QUOTE_CTX_KEY  = 'cwpe_quote_context'
const ACTIVE_MUNI_KEY = 'cwpe_active_muni'
const BUNDLE_KEY     = 'cwpe_selected_bundle'
const MATERIAL_DELTA_PCT = 0.05
const POP_BANDS = [500, 1000, 1500, 2000, 2500, 3000, 5000, 7500, 10000, 15000, 25000]

export default {
	name: 'CwpePricing',

	data() {
		return {
			loading: false,
			config: null,
			competitors: [],

			// Simulator state — population stays null until a muni is picked
			// (or the rep types one in). Avoids the old "10k snaps over the
			// real number" trap when ACS came back empty.
			sim: {
				population: null,
				populationTrendPct: 0.02,
				countyTrendPct: 0.01,
				stateTrendPct: 0.01,
				snapRate: 0.10,
				state: 'WI',
				supportTierId: 'standard',
			},
			simResult: null,
			simLoading: false,

			// Municipality search
			muniQuery: '',
			muniSuggestions: [],
			muniSearching: false,
			muniShowDropdown: false,
			muniActiveIdx: -1,
			muniLoading: false,
			muniLoaded: null,
			muniLoadedId: null,
			muniError: null,
			muniSearchTimer: null,

			// Quote context
			mode: 'new',
			contractYears: 1,
			priorAnnualDollars: null,
			targetIncreasePct: 0,

			// Competitor comparison
			selectedBundleId: 'b1',
			showOutOfSegment: false,
			horizon: 1,
		}
	},

	created() {
		// Restore quote context from localStorage (shared with Lookup page)
		try {
			const ctx = JSON.parse(localStorage.getItem(QUOTE_CTX_KEY) || 'null')
			if (ctx) {
				if (ctx.mode)                                    this.mode               = ctx.mode
				if (ctx.contractYears)                           this.contractYears      = ctx.contractYears
				if (ctx.priorAnnualDollars !== undefined)        this.priorAnnualDollars = ctx.priorAnnualDollars
				if (ctx.targetIncreasePct  !== undefined)        this.targetIncreasePct  = ctx.targetIncreasePct
			}
		} catch (_) { /* ignore */ }

		// Restore cross-page bundle selection (shared with Lookup page)
		try {
			const saved = localStorage.getItem(BUNDLE_KEY)
			if (saved) this.selectedBundleId = saved
		} catch (_) { /* ignore */ }

		this.loadAll()
	},

	mounted() {
		document.addEventListener('mousedown', this.onDocMouseDown)
		window.addEventListener('cwpe:muni-refreshing', this.onMuniRefreshing)
		window.addEventListener('cwpe:muni-refreshed', this.onMuniRefreshed)
	},

	beforeDestroy() {
		document.removeEventListener('mousedown', this.onDocMouseDown)
		window.removeEventListener('cwpe:muni-refreshing', this.onMuniRefreshing)
		window.removeEventListener('cwpe:muni-refreshed', this.onMuniRefreshed)
	},

	watch: {
		'sim.population'()         { this.runSim() },
		'sim.populationTrendPct'() { this.runSim() },
		'sim.countyTrendPct'()     { this.runSim() },
		'sim.stateTrendPct'()      { this.runSim() },
		'sim.snapRate'()           { this.runSim() },
		'sim.state'()              { this.runSim() },
		'sim.supportTierId'()      { this.runSim() },
		mode()                     { this.runSim() },
		contractYears()            { this.runSim() },
		priorAnnualDollars()       { this.runSim() },
		targetIncreasePct()        { this.runSim() },
		selectedBundleId(v) { try { localStorage.setItem(BUNDLE_KEY, v) } catch (e) { /* ignore */ } },
	},

	computed: {

		// ── Growth interpretation ──────────────────────────────────────
		growthInterpretation() {
			const muni   = this.sim.populationTrendPct
			const county = this.sim.countyTrendPct
			const state  = this.sim.stateTrendPct
			if (muni > county && muni > state) {
				return {
					tone:     'positive',
					headline: `Outgrowing the region — pricing factor +5%`,
					body:     `Muni (${this.fmtPct(muni)}) beats both county (${this.fmtPct(county)}) and state (${this.fmtPct(state)}). Growing faster than neighbors signals a healthier tax base and willingness to invest, so we nudge price up.`,
				}
			}
			if (muni < county && muni < state) {
				return {
					tone:     'negative',
					headline: `Below regional growth — pricing factor −5%`,
					body:     `Muni (${this.fmtPct(muni)}) trails both county (${this.fmtPct(county)}) and state (${this.fmtPct(state)}). Shrinking relative to neighbors means tighter budgets, so we nudge price down.`,
				}
			}
			return {
				tone:     'neutral',
				headline: 'Mixed growth — no pricing change',
				body:     `Muni (${this.fmtPct(muni)}) is between county (${this.fmtPct(county)}) and state (${this.fmtPct(state)}), or matches one. Needs to beat BOTH (or lag BOTH) to move price.`,
			}
		},

		// ── SNAP interpretation ────────────────────────────────────────
		snapInterpretation() {
			const r = this.sim.snapRate
			if (r == null || isNaN(r)) {
				return { tone: 'neutral', headline: 'No SNAP rate provided', body: '' }
			}
			if (r >= 0.12) {
				return {
					tone:     'negative',
					headline: `High SNAP rate — pricing factor −5%`,
					body:     `${this.fmtPct(r)} is at or above the 12% threshold. Higher share of households on food assistance = less budget flexibility for municipal vendors, so we discount.`,
				}
			}
			if (r <= 0.08) {
				return {
					tone:     'positive',
					headline: `Low SNAP rate — pricing factor +3%`,
					body:     `${this.fmtPct(r)} is at or below 8%. Lower SNAP share typically means a more affluent tax base, so we can price on the higher end.`,
				}
			}
			return {
				tone:     'neutral',
				headline: 'Mid-range SNAP — no pricing change',
				body:     `${this.fmtPct(r)} is between 8% and 12%. No adjustment until it crosses a threshold.`,
			}
		},

		// ── Adjustment factors from sim result ─────────────────────────
		adjustmentFactors() {
			if (!this.simResult) return []
			return (this.simResult.adjustmentFactors?.factors || [])
		},

		// ── Competitor comparison helpers ──────────────────────────────
		selectedBundle() {
			if (!this.config) return null
			return this.config.bundles.find(b => b.id === this.selectedBundleId) || this.config.bundles[0]
		},

		visibleCompetitors() {
			const pop = this.sim.population
			if (this.showOutOfSegment || pop == null) return this.competitors
			return this.competitors.filter(c => {
				if (pop < c.minPopulation) return false
				if (c.maxPopulation != null && pop > c.maxPopulation) return false
				return true
			})
		},

		hiddenCompetitors() {
			const visible = new Set(this.visibleCompetitors.map(c => c.id))
			return this.competitors.filter(c => !visible.has(c.id))
		},

		anyEstimated() {
			return this.visibleCompetitors.some(c => c.pricingStatus !== 'verified')
		},

		visibleBySegment() {
			const byCat = {}
			for (const c of this.visibleCompetitors) {
				if (!byCat[c.segment]) byCat[c.segment] = []
				byCat[c.segment].push(c)
			}
			const LABELS = { 'small-muni': 'Small-muni tier', 'mid-tier': 'Mid-tier' }
			return ['small-muni', 'mid-tier']
				.filter(s => byCat[s]?.length)
				.map(s => ({ segment: s, label: LABELS[s], names: byCat[s].map(c => c.name) }))
		},

		// Comparison table rows: actual muni + band populations, sorted
		compRows() {
			const out = []
			const actual = this.sim.population
			if (actual != null) {
				out.push({
					pop:      actual,
					label:    this.muniLoaded
						? `${this.muniLoaded.name}, ${this.muniLoaded.state} (${actual.toLocaleString('en-US')})`
						: `${actual.toLocaleString('en-US')} (actual)`,
					isActual: true,
				})
			}
			for (const b of POP_BANDS) {
				if (actual != null && b === actual) continue
				out.push({ pop: b, label: b.toLocaleString('en-US'), isActual: false })
			}
			out.sort((a, b) => a.pop - b.pop)
			return out
		},

		// Bundle-annualized strip at current muni pop
		tierAnnualized() {
			if (!this.config || this.sim.population == null) return []
			return this.config.bundles.map(b => {
				const q = this.computeBundlePrice(this.sim.population, b)
				if (!q) return null
				const horizonTotal = this.totalForHorizon(q.setupCents, q.annualCents)
				return {
					bundleId:             b.id,
					bundleName:           b.name,
					setupCents:           q.setupCents,
					annualCents:          q.annualCents,
					horizonTotalCents:    horizonTotal,
					effectiveAnnualCents: Math.round(horizonTotal / this.horizon),
					firstYearCents:       q.firstYearCents,
					deltaVsPriorYearPct:  q.deltaVsPriorYearPct,
					onTarget:             q.onTarget,
				}
			}).filter(Boolean)
		},

		// Target-range bar data (upgrade mode only)
		targetRange() {
			if (this.mode !== 'upgrade' || !this.priorAnnualDollars) return null
			const prior  = Math.round(this.priorAnnualDollars * 100)
			const target = Math.round(prior * (1 + (this.targetIncreasePct || 0) / 100))
			const tolerancePct = 0.03
			const dots   = this.tierAnnualized
			const bundleMax = dots.length ? Math.max(...dots.map(d => d.firstYearCents)) : target
			const rawMax = Math.max(bundleMax, Math.round(target * (1 + tolerancePct)))
			const maxCents = Math.round(Math.max(rawMax, prior) * 1.05)
			const range  = (maxCents - prior) || 1
			const toPct  = c => Math.max(0, Math.min(100, ((c - prior) / range) * 100))
			const toleranceLo = Math.round(target * (1 - tolerancePct))
			const toleranceHi = Math.round(target * (1 + tolerancePct))
			const competitorTicks = this.visibleCompetitors.map(c => {
				const t = this.competitorHorizonTotal(c, this.sim.population)
				if (t == null) return null
				return { name: c.name, cents: t, leftPct: toPct(t) }
			}).filter(Boolean)
			return {
				priorCents:         prior,
				targetCents:        target,
				targetLeftPct:      toPct(target),
				maxCents,
				toleranceLeftPct:   toPct(toleranceLo),
				toleranceRightPct:  toPct(toleranceHi),
				dots: dots.map(d => ({
					bundleId:       d.bundleId,
					bundleName:     d.bundleName,
					firstYearCents: d.firstYearCents,
					leftPct:        toPct(d.firstYearCents),
					onTarget:       d.onTarget,
				})),
				competitorTicks,
			}
		},

		// Competitor insights
		competitorInsights() {
			const out = []
			const cp = this.sim.population
			if (cp == null || !this.visibleCompetitors.length) return out
			const ourTotal = this.ourHorizonTotal(cp)
			if (ourTotal == null) return out
			for (const c of this.visibleCompetitors) {
				const theirs = this.competitorHorizonTotal(c, cp)
				if (theirs == null) continue
				const d = this.computeDelta(ourTotal, theirs)
				if (!d || d.stance === 'on-par') {
					if (d) out.push(`At pop ${cp.toLocaleString()}, vs ${c.name}: roughly on par.`)
					continue
				}
				const dollars = `$${Math.abs(Math.round(d.diff / 100)).toLocaleString()}`
				const pct = `${Math.round(Math.abs(d.pct) * 100)}%`
				const estTag = c.pricingStatus !== 'verified' ? ' (est.)' : ''
				out.push(
					d.stance === 'cheaper'
						? `We undercut ${c.name}${estTag} by ${dollars} (${pct}) over ${this.horizon} year${this.horizon === 1 ? '' : 's'}.`
						: `${c.name}${estTag} undercuts us by ${dollars} (${pct}) over ${this.horizon} year${this.horizon === 1 ? '' : 's'}.`,
				)
			}
			return out
		},
	},

	methods: {

		// ── Data loading ───────────────────────────────────────────────
		async loadAll() {
			this.loading = true
			try {
				const [cfgRes, compRes] = await Promise.all([
					restApi.get('/cwpe/pricing/config'),
					restApi.get('/cwpe/competitors'),
				])
				this.config      = cfgRes.data
				this.competitors = compRes.data.competitors || []
				// Default to first bundle only if none is saved
				if (this.config.bundles?.length && !localStorage.getItem(BUNDLE_KEY)) {
					this.selectedBundleId = this.config.bundles[0].id
				}
			} catch (err) {
				this.$swal.fire({ icon: 'error', title: 'Failed to load config', text: err?.response?.data?.error || '' })
			} finally {
				this.loading = false
			}

			// Auto-load active municipality so simulator is pre-filled (syncs with Lookup page)
			try {
				const savedMuni = JSON.parse(localStorage.getItem(ACTIVE_MUNI_KEY) || 'null')
				if (savedMuni?.id) {
					await this.loadFromMunicipality(savedMuni)
				}
			} catch (_) { /* ignore */ }

			await this.runSim()
		},

		async runSim() {
			if (!this.config) return
			if (this.sim.population == null) { this.simResult = null; return }
			this.simLoading = true
			try {
				const payload = {
					population:            this.sim.population,
					population_trend_pct:  this.sim.populationTrendPct,
					county_trend_pct:      this.sim.countyTrendPct,
					state_trend_pct:       this.sim.stateTrendPct,
					snap_rate:             this.sim.snapRate,
					state:                 this.sim.state,
					support_tier_id:       this.sim.supportTierId,
					mode:                  this.mode,
					contract_years:        this.contractYears,
					prior_year_annual_cents: this.mode === 'upgrade' && this.priorAnnualDollars
						? Math.round(this.priorAnnualDollars * 100) : null,
					target_increase_pct: this.mode === 'upgrade' ? this.targetIncreasePct / 100 : null,
				}
				const { data } = await restApi.post('/cwpe/pricing/simulate', payload)
				this.simResult = data
				// Same fix as Lookup: when the saved bundle is no longer in
				// the recommended set for this population (e.g. rep ran a
				// 100k pop sim earlier and saved b5, then drops to 300 pop
				// where only b1/b2 are appropriate) — switch to first
				// recommended.
				const recommendedList = (data && data.recommendedBundleIds) || []
				if (this.selectedBundleId && recommendedList.length && !recommendedList.includes(this.selectedBundleId)) {
					this.selectedBundleId = recommendedList[0]
				}
			} catch (_) { /* silently ignore simulation errors */ } finally {
				this.simLoading = false
			}
		},

		// ── QuoteContextBar ────────────────────────────────────────────
		saveQuoteCtx() {
			localStorage.setItem(QUOTE_CTX_KEY, JSON.stringify({
				mode:               this.mode,
				contractYears:      this.contractYears,
				priorAnnualDollars: this.priorAnnualDollars,
				targetIncreasePct:  this.targetIncreasePct,
			}))
		},
		setMode(m)            { this.mode          = m; this.saveQuoteCtx() },
		setContractYears(y)   { this.contractYears = y; this.saveQuoteCtx() },
		onCtxChanged()        { this.saveQuoteCtx(); this.runSim(); this.persistPriorAnnual() },
		clearPriorAnnual()    { this.priorAnnualDollars = null; this.saveQuoteCtx(); this.persistPriorAnnual() },

		// Persist prior year annual to the DB so it's available cross-session
		persistPriorAnnual() {
			if (!this.muniLoadedId) return
			restApi.post('/cwpe/pricing/municipality-data', {
				municipality_id: this.muniLoadedId,
				prior_year_annual_cents: this.priorAnnualDollars != null
					? Math.round(this.priorAnnualDollars * 100) : null,
			}).catch(() => { /* ignore */ })
		},

		// ── Municipality search (for left panel) ──────────────────────
		muniDebounceSearch() {
			if (this.muniSearchTimer) clearTimeout(this.muniSearchTimer)
			const q = this.muniQuery.trim()
			if (q.length < 3) {
				this.muniSuggestions   = []
				this.muniShowDropdown  = false
				this.muniSearching     = false
				return
			}
			this.muniShowDropdown = true
			this.muniSearching    = true
			this.muniSearchTimer  = setTimeout(() => {
				restApi.get('/municipalities/search', { params: { search: q } })
					.then(({ data }) => {
						this.muniSuggestions = (Array.isArray(data) ? data : (data.results || data.data || [])).slice(0, 10)
						this.muniActiveIdx   = -1
					})
					.catch(() => { this.muniSuggestions = [] })
					.finally(() => { this.muniSearching = false })
			}, 300)
		},

		async loadFromMunicipality(s) {
			this.muniShowDropdown = false
			this.muniQuery        = `${s.full_name || s.name}, ${s.state}`
			this.muniLoading      = true
			this.muniError        = null
			try {
				const { data } = await restApi.post('/cwpe/pricing/lookup', {
					municipality_id: s.id,
					name:            s.full_name || s.name,
					state:           s.state,
					mode:            this.mode,
					contract_years:  this.contractYears,
				})
				const md = data.municipality_data
				if (md) {
					// Priority: Census ACS → backend muni-table fallback → the
					// autocomplete row's own population. We never want the old
					// 10k placeholder to leak past this point.
					const resolvedPop = md.population != null
						? md.population
						: (data.muni_fallback_population != null
							? data.muni_fallback_population
							: (s.population != null ? s.population : null))
					if (resolvedPop != null)             this.sim.population           = resolvedPop
					if (md.population_trend_pct != null) this.sim.populationTrendPct   = md.population_trend_pct
					if (md.snap_rate != null)            this.sim.snapRate              = md.snap_rate
					if (s.state)                         this.sim.state                 = s.state
					// Set county/state trend context if the lookup enriched it
					if (md.context_data) {
						const ctx = md.context_data
						if (ctx.county?.populationTrendPct != null) this.sim.countyTrendPct = ctx.county.populationTrendPct
						if (ctx.state?.populationTrendPct  != null) this.sim.stateTrendPct  = ctx.state.populationTrendPct
					}
					// Auto-populate prior year anchor if municipality has stored data (mirrors original applyLookupResult)
					if (md.prior_year_annual_cents != null && this.priorAnnualDollars == null) {
						this.priorAnnualDollars = md.prior_year_annual_cents / 100
						if (this.mode === 'new') this.mode = 'upgrade'
						this.saveQuoteCtx()
					}
				}
				this.muniLoaded = { name: s.full_name || s.name, state: s.state, county: s.county || null }
				this.muniLoadedId = s.id || null
			} catch (err) {
				this.muniError = err?.response?.data?.error || err?.message || 'Lookup failed.'
			} finally {
				this.muniLoading = false
			}
		},

		clearMuniLoaded() {
			this.muniLoaded   = null
			this.muniLoadedId = null
			this.muniQuery    = ''
		},

		// Parent's "Refresh" button started — enter the page-level loading state
		// immediately so the whole page shows the initial-load spinner for the
		// full duration of the force-refresh, not just a flash at the end.
		onMuniRefreshing() {
			this.loading = true
		},

		// Parent's "Refresh" button just force-refreshed the cache — re-pull the
		// active muni so the simulator reflects the fresh population/trends/SNAP.
		// Drive the page-level `loading` flag so the whole page shows the same
		// loading state as the initial load while the re-pull runs.
		async onMuniRefreshed() {
			let m = null
			try {
				m = JSON.parse(localStorage.getItem(ACTIVE_MUNI_KEY) || 'null')
			} catch (_) { /* ignore */ }
			if (!m || !m.id) return
			this.loading = true
			try {
				await this.loadFromMunicipality(m)
			} finally {
				this.loading = false
			}
		},

		muniKeydown(e) {
			if (e.key === 'Escape' && this.muniShowDropdown) {
				e.preventDefault()
				this.muniShowDropdown = false
				return
			}
			if (!this.muniShowDropdown || !this.muniSuggestions.length) return
			if (e.key === 'ArrowDown') {
				e.preventDefault()
				this.muniActiveIdx = Math.min(this.muniActiveIdx + 1, this.muniSuggestions.length - 1)
			} else if (e.key === 'ArrowUp') {
				e.preventDefault()
				this.muniActiveIdx = Math.max(this.muniActiveIdx - 1, 0)
			} else if (e.key === 'Enter' && this.muniActiveIdx >= 0) {
				e.preventDefault()
				this.loadFromMunicipality(this.muniSuggestions[this.muniActiveIdx])
			}
		},

		onDocMouseDown(e) {
			if (!this.muniShowDropdown) return
			const box = this.$refs.muniSearchBox
			if (box && !box.contains(e.target)) this.muniShowDropdown = false
		},

		// ── Sim bundle helper (recommends) ─────────────────────────────
		isBundleRecommended(bundleId) {
			return this.simResult?.recommendedBundleIds?.includes(bundleId) || false
		},

		toggleBundle(bundleId) {
			this.selectedBundleId = this.selectedBundleId === bundleId ? (this.config?.bundles[0]?.id || 'b1') : bundleId
		},

		// ── Local pricing formula (for competitor comparison table) ────
		computeAdjustmentPct() {
			if (!this.config) return 0
			const s   = this.config.settings
			const sim = this.sim
			let adj   = 0

			const muni   = sim.populationTrendPct || 0
			const county = sim.countyTrendPct     || 0
			const state  = sim.stateTrendPct      || 0
			if (muni > county && muni > state)      adj += s.growth_leader_pct  || 0.05
			else if (muni < county && muni < state) adj += s.growth_laggard_pct || -0.05

			const snapRate = sim.snapRate || 0
			if      (snapRate >= (s.snap_high_threshold || 0.12)) adj += s.snap_high_pct || -0.05
			else if (snapRate <= (s.snap_low_threshold  || 0.08)) adj += s.snap_low_pct  ||  0.03

			const stateCode = (sim.state || '').toUpperCase()
			if (stateCode && s.state_adjust && s.state_adjust[stateCode] != null) {
				adj += s.state_adjust[stateCode]
			}
			return adj
		},

		computeBundlePrice(pop, bundle) {
			if (!this.config || pop == null) return null
			const s  = this.config.settings
			const b1Setup  = Math.round((s.setup_fixed_cents  + s.setup_per_pop_cents  * pop) * (1 + (s.base_markup_pct || 0)))
			const b1Annual = Math.round((s.annual_fixed_cents + s.annual_per_pop_cents * pop) * (1 + (s.base_markup_pct || 0)))
			const rawSetup  = Math.round(b1Setup  * bundle.multiplier)
			const rawAnnual = Math.round(b1Annual * bundle.multiplier)

			// Support surcharge (use sim's tier)
			const tier = (this.config.supportTiers || []).find(t => t.id === this.sim.supportTierId) || { surcharge: 0 }
			const supportSurcharge = Math.round(rawAnnual * (tier.surcharge || 0))
			const rawAnnualWithSupport = rawAnnual + supportSurcharge

			const adjPct = this.computeAdjustmentPct()
			let setupCents  = Math.round(rawSetup * (1 + adjPct))
			let annualCents = Math.round(rawAnnualWithSupport * (1 + adjPct))

			if (this.mode === 'upgrade') {
				const mult = s.upgrade_setup_multiplier || 0.5
				const min  = s.upgrade_setup_min_cents  || 25000
				setupCents = Math.max(Math.round(setupCents * mult), min)
			}

			const contractMults = s.contract_term_multipliers || { 1: 1.0, 2: 0.97, 3: 0.93, 4: 0.90 }
			const termMult = contractMults[this.contractYears] || 1.0
			if (termMult !== 1.0) annualCents = Math.round(annualCents * termMult)

			const firstYearCents = setupCents + annualCents
			const priorAnnual = this.mode === 'upgrade' && this.priorAnnualDollars
				? Math.round(this.priorAnnualDollars * 100) : null
			const targetPct  = (this.targetIncreasePct || 0) / 100
			const targetCents = priorAnnual != null ? priorAnnual * (1 + targetPct) : null
			const deltaVsPriorYearPct = priorAnnual != null && priorAnnual > 0
				? (firstYearCents - priorAnnual) / priorAnnual : null
			const onTarget = this.mode === 'upgrade' && priorAnnual != null && targetCents != null
				? Math.abs(firstYearCents - targetCents) / targetCents <= 0.03 : false

			return { setupCents, annualCents, firstYearCents, deltaVsPriorYearPct, onTarget }
		},

		// ourPrice using currently selected bundle (for competitor table)
		compOurPrice(pop) {
			if (!this.selectedBundle) return null
			return this.computeBundlePrice(pop, this.selectedBundle)
		},

		totalForHorizon(setupCents, annualCents) {
			return setupCents + annualCents * this.horizon
		},

		ourHorizonTotal(pop) {
			const q = this.compOurPrice(pop)
			if (!q) return null
			return this.totalForHorizon(q.setupCents, q.annualCents)
		},

		ourEffectiveAnnual(pop) {
			const t = this.ourHorizonTotal(pop)
			return t == null ? null : Math.round(t / this.horizon)
		},

		// ── Competitor helpers ─────────────────────────────────────────
		competitorBand(c, pop) {
			for (const b of c.bands) {
				const cap = b.upToPop == null ? Infinity : b.upToPop
				if (pop <= cap) return b
			}
			return null
		},

		competitorHorizonTotal(c, pop) {
			const band = this.competitorBand(c, pop)
			if (!band) return null
			return this.totalForHorizon(band.setupCents || 0, band.annualCents)
		},

		competitorEffectiveAnnual(c, pop) {
			const t = this.competitorHorizonTotal(c, pop)
			return t == null ? null : Math.round(t / this.horizon)
		},

		verdictForPop(pop) {
			const ours = this.ourHorizonTotal(pop)
			if (ours == null) return 'no-data'
			const vs = this.visibleCompetitors
				.map(c => this.competitorHorizonTotal(c, pop))
				.filter(v => v != null)
			if (!vs.length) return 'no-data'
			const cheaper      = vs.every(t => (ours - t) / t < -MATERIAL_DELTA_PCT)
			const moreExpensive = vs.every(t => (ours - t) / t > MATERIAL_DELTA_PCT)
			if (cheaper)       return 'undercut'
			if (moreExpensive) return 'beaten'
			return 'split'
		},

		computeDelta(ourCents, theirCents) {
			if (ourCents == null || theirCents == null || theirCents === 0) return null
			const diff  = ourCents - theirCents
			const pct   = diff / theirCents
			const stance = Math.abs(pct) < MATERIAL_DELTA_PCT
				? 'on-par'
				: pct < 0 ? 'cheaper' : 'more-expensive'
			return { diff, pct, stance }
		},

		stanceStyle(delta) {
			if (!delta || delta.stance === 'on-par') return 'color:#475569;'
			if (delta.stance === 'cheaper')          return 'color:#065f46;background:#ecfdf5;font-weight:600;'
			return 'color:#9f1239;background:#fff1f2;font-weight:600;'
		},

		// ── Tone/color helpers ─────────────────────────────────────────
		toneStyle(tone) {
			if (tone === 'positive') return 'background:#ecfdf5;color:#065f46;box-shadow:inset 0 0 0 1px #a7f3d0;'
			if (tone === 'negative') return 'background:#fff1f2;color:#9f1239;box-shadow:inset 0 0 0 1px #fecdd3;'
			return 'background:#f8fafc;color:#334155;box-shadow:inset 0 0 0 1px #e2e8f0;'
		},

		trendValueColor(value, vs) {
			if (value > vs) return 'color:#047857;'
			if (value < vs) return 'color:#be123c;'
			return 'color:#475569;'
		},

		// ── Formatters ────────────────────────────────────────────────
		fmtMoney(cents) {
			if (cents == null) return '—'
			return '$' + Math.round(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })
		},

		fmtPct(d) {
			if (d == null || isNaN(d)) return '—'
			return (d * 100).toFixed(1) + '%'
		},
	},
}
</script>

<style scoped>
/* QuoteContextBar shared styles (keep in sync with CwpeLookup.vue) */
.cwpe-label {
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #475569;
}
.cwpe-label-amber { color: #92400e !important; }
.cwpe-sublabel { font-size: 11px; color: #64748b; }
.cwpe-ctx-btn {
	padding: 4px 12px;
	font-size: 12px;
	font-weight: 500;
	background: none;
	border: none;
	cursor: pointer;
	line-height: 1.4;
}
.cwpe-ctx-btn-primary  { background: #2563eb; color: #fff; }
.cwpe-ctx-btn-upgrade  { background: #d97706; color: #fff; }
.cwpe-ctx-btn-dark     { background: #0f172a; color: #fff; }
.cwpe-ctx-btn-idle     { color: #475569; }
.cwpe-ctx-btn-idle:hover { background: #f1f5f9; }
.cwpe-btn-group { overflow: hidden; }
.cwpe-upgrade-input { border-color: #fcd34d !important; }
.cwpe-upgrade-input:focus { border-color: #d97706 !important; box-shadow: 0 0 0 2px rgba(217,119,6,0.15) !important; }
.cwpe-dollar-sign { font-size: 13px; }
.cwpe-range {
	accent-color: #d97706;
	cursor: pointer;
	display: block;
}
.cwpe-pct-badge {
	min-width: 2.8rem;
	text-align: right;
	color: #c2410c;
	font-size: 16px;
	font-weight: 700;
	font-family: ui-monospace, monospace;
	white-space: nowrap;
}
</style>
